import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ElementType, type ReactNode } from 'react';
import { focusRing } from './_shared';

const Base = chakra('a', {
  base: {
    fontFamily: 'body',
    color: 'text.link',
    cursor: 'pointer',
    borderRadius: 'xs',
    textDecoration: 'none',
    transitionProperty: 'color, text-decoration-color',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    _hover: { textDecoration: 'underline', textUnderlineOffset: '2px' },
    ...focusRing,
  },
});

export interface LinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'color'> {
  /** Render as a different element/component (e.g. a router Link). */
  as?: ElementType;
  /**
   * Merge the link's styles onto a single child element instead of rendering its own
   * `<a>` (chakra's Slot pattern). Use this to style a router `Link` while letting its
   * OWN navigation props (`to`/`params`/`search`) reach it unfiltered — chakra's `as`
   * forwarding drops non-DOM object props like `params`/`search`, so a typed router link
   * must be the child, not `as`.
   */
  asChild?: boolean;
  /** Quieter link that inherits surrounding text color until hover. */
  muted?: boolean;
  /** Always show the underline (for links inside running prose). */
  underline?: boolean;
  external?: boolean;
  children?: ReactNode;
}

/** Inline navigation. Token-driven color + always keyboard-focusable with a ring. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { as, asChild, muted, underline, external, ...rest },
  ref,
) {
  const sx: HTMLChakraProps<'a'> = {
    ...(muted ? { color: 'text.secondary', _hover: { color: 'text.link', textDecoration: 'underline' } } : {}),
    ...(underline ? { textDecoration: 'underline', textUnderlineOffset: '2px' } : {}),
  };
  const externalProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  // `asChild` merges styles onto the child (Slot); `as` swaps the element. They are mutually
  // exclusive — `asChild` wins so a typed router link child keeps its own nav props.
  return <Base as={asChild ? undefined : as} asChild={asChild} ref={ref} {...externalProps} {...sx} {...rest} />;
});
